import * as T from 'three';
import { loadModel, modelUrl } from '@london-life/game/scene/models';

// The map's buildings, trees, cars and lamps as models from the library, drawn the cheap way: every copy of one
// model is a single instanced draw, so hundreds of houses cost about what one does. Each copy first stands as
// blocks (its holder); once its model has loaded, the blocks are hidden and the model takes their place.

type Placement = {
  holder: T.Object3D;
  // the space it fills: footprint, and the height to reach (kept within the model's own proportions)
  w: number;
  d: number;
  h?: number;
  // a quarter turn for models made facing another way (cars)
  yaw: number;
};

// a kit's other colour sheet ("suburban/Textures/variation-b.png"), loaded once and shared
const sheets = new Map<string, Promise<T.Texture>>();
function loadSheet(file: string) {
  let sheet = sheets.get(file);
  if (!sheet) {
    sheet = new T.TextureLoader().loadAsync(modelUrl(file)).catch((error) => {
      sheets.delete(file);
      throw error;
    });
    sheets.set(file, sheet);
  }
  return sheet;
}

export function createInstancer(group: T.Group) {
  const wanted = new Map<string, Placement[]>();
  const made: T.InstancedMesh[] = [];
  let cancelled = false;

  // `sheet`: one of the kit's other colour sheets (red brick, grey slate) instead of the model's own
  const add = (
    model: string,
    holder: T.Object3D,
    fit: { w: number; d: number; h?: number },
    yaw = 0,
    sheet?: string,
  ) => {
    const key = sheet ? `${model}|${sheet}` : model;
    const list = wanted.get(key) ?? [];
    list.push({ holder, ...fit, yaw });
    wanted.set(key, list);
  };
  const owned: T.Material[] = [];
  // the model's materials with the other colour sheet in place of their own
  const recolour = (material: T.Material | T.Material[], sheet: T.Texture): T.Material | T.Material[] => {
    if (Array.isArray(material)) return material.map((one) => recolour(one, sheet) as T.Material);
    const own = (material as T.MeshStandardMaterial).map;
    if (!own) return material;
    const copy = (material as T.MeshStandardMaterial).clone();
    const map = sheet.clone();
    map.flipY = own.flipY;
    map.colorSpace = own.colorSpace;
    map.magFilter = own.magFilter;
    map.minFilter = own.minFilter;
    map.wrapS = own.wrapS;
    map.wrapT = own.wrapT;
    map.offset.copy(own.offset);
    map.repeat.copy(own.repeat);
    map.rotation = own.rotation;
    map.center.copy(own.center);
    map.needsUpdate = true;
    copy.map = map;
    owned.push(copy);
    return copy;
  };

  const place = (model: string, source: T.Object3D, list: Placement[], sheet?: T.Texture) => {
    source.updateMatrixWorld(true);
    const box = new T.Box3().setFromObject(source);
    const own = box.getSize(new T.Vector3());
    const centre = box.getCenter(new T.Vector3());
    const parts: { geometry: T.BufferGeometry; material: T.Material | T.Material[]; matrix: T.Matrix4 }[] = [];
    source.traverse((object) => {
      if (object instanceof T.Mesh)
        parts.push({ geometry: object.geometry, material: object.material, matrix: object.matrixWorld.clone() });
    });
    const meshes = parts.map((part) => {
      const mesh = new T.InstancedMesh(
        part.geometry,
        sheet ? recolour(part.material, sheet) : part.material,
        list.length,
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.name = model;
      return mesh;
    });
    const local = new T.Matrix4(),
      turn = new T.Matrix4(),
      size = new T.Matrix4(),
      shift = new T.Matrix4(),
      result = new T.Matrix4();
    list.forEach((item, index) => {
      item.holder.updateMatrix();
      // the model's own x and z after its quarter turn
      const across = item.yaw % Math.PI === 0 ? own.x : own.z;
      const deep = item.yaw % Math.PI === 0 ? own.z : own.x;
      const flat = Math.min(item.w / Math.max(across, 0.01), item.d / Math.max(deep, 0.01));
      const tall = item.h ? T.MathUtils.clamp(item.h / Math.max(own.y, 0.01), flat * 0.8, flat * 1.6) : flat;
      local.copy(item.holder.matrix);
      turn.makeRotationY(item.yaw);
      size.makeScale(flat, tall, flat);
      shift.makeTranslation(-centre.x, -box.min.y, -centre.z);
      parts.forEach((part, which) => {
        result.copy(local).multiply(turn).multiply(size).multiply(shift).multiply(part.matrix);
        meshes[which]!.setMatrixAt(index, result);
      });
      item.holder.visible = false;
    });
    for (const mesh of meshes) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      group.add(mesh);
      made.push(mesh);
    }
  };

  // load every model and swap them in; resolves once all that could load are in (a failed one keeps its blocks)
  const build = () =>
    Promise.all(
      [...wanted].map(([key, list]) => {
        const [model, sheet] = key.split('|') as [string, string | undefined];
        return Promise.all([loadModel(model), sheet ? loadSheet(sheet) : undefined])
          .then(([source, texture]) => {
            if (!cancelled) place(model, source, list, texture);
          })
          .catch(() => {
            // the blocks stay
          });
      }),
    ).then(() => undefined);

  return {
    add,
    build,
    dispose() {
      cancelled = true;
      for (const mesh of made) mesh.dispose();
      for (const material of owned) {
        (material as T.MeshStandardMaterial).map?.dispose();
        material.dispose();
      }
    },
  };
}
export type Instancer = ReturnType<typeof createInstancer>;
